import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/components/ui/button";
import { Camera, Calendar, IdCard, ShieldCheck, BadgeCheck, UserRound, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { kycUiStore } from "@/lib/kycUiState";
import { tAuto } from "@/lib/tAuto";

interface VerifyAccountModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName?: string | null;
}

const steps = [
  {
    icon: Camera,
    title: "Tirar selfie",
    desc: "Uma foto sua para confirmar que é você.",
  },
  {
    icon: Calendar,
    title: tAuto("verifyAccount.steps.birthDate", "Data de nascimento"),
    desc: "Confirme a data para validar a idade.",
  },
  {
    icon: IdCard,
    title: tAuto("verifyAccount.steps.idDocument", "Documento de identificação"),
    desc: "BI (frente e verso) ou passaporte.",
  },
  {
    icon: ShieldCheck,
    title: tAuto("verifyAccount.steps.submit", "Enviar para análise"),
    desc: "A nossa equipa verifica em poucos minutos.",
  },
];

export function VerifyAccountModal({ open, onOpenChange, userName }: VerifyAccountModalProps) {
  const navigate = useNavigate();

  const firstName = (userName || "").trim().split(/\s+/)[0] || null;

  // Centralized close: clears session flag, releases the overlay lock and
  // scrubs any stray body styles Radix/Framer may leave behind (root cause
  // of the "screen frozen after close" reports).
  const closeAndCleanup = () => {
    try {
      sessionStorage.removeItem("bera:show_verify_modal");
      sessionStorage.setItem("bera:verify_dismissed_at", String(Date.now()));
    } catch {}
    onOpenChange(false);
    kycUiStore.setOpen(false);
    const scrubBody = () => {
      try {
        document.body.style.pointerEvents = "";
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      } catch {}
    };
    scrubBody();
    requestAnimationFrame(scrubBody);
    window.setTimeout(scrubBody, 80);
  };

  const handleVerifyNow = () => {
    closeAndCleanup();
    navigate("/perfil", { state: { openKyc: true } });
  };

  const handleLater = () => {
    closeAndCleanup();
  };

  // Hide floating widgets (like Bera IA) while this modal is open,
  // and force-cleanup on unmount to prevent frozen UI.
  useEffect(() => {
    kycUiStore.setOpen(open);
    if (!open) {
      try {
        document.body.style.pointerEvents = "";
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      } catch {}
    }
    return () => {
      kycUiStore.setOpen(false);
      try {
        document.body.style.pointerEvents = "";
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      } catch {}
    };
  }, [open]);

  return (
    <DialogPrimitive.Root
      modal
      open={open}
      onOpenChange={(v) => {
        if (!v) closeAndCleanup();
        else onOpenChange(v);
      }}
    >
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[10000] bg-black/50 backdrop-blur-[2px]"
              />
            </DialogPrimitive.Overlay>

            <div
              className="fixed inset-0 z-[10001] pointer-events-none flex items-center justify-center p-3 sm:p-4"
            >
              <DialogPrimitive.Content
                asChild
                onOpenAutoFocus={(e) => e.preventDefault()}
                onPointerDownOutside={closeAndCleanup}
                onEscapeKeyDown={closeAndCleanup}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.94, y: 12 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.94, y: 12 }}
                  transition={{ opacity: { duration: 0.18 }, scale: { duration: 0.2, ease: "easeOut" }, y: { duration: 0.2, ease: "easeOut" } }}
                  className="relative pointer-events-auto w-[calc(100vw-24px)] sm:w-[92vw] max-w-[440px] max-h-[calc(100dvh-32px)] overflow-y-auto overscroll-contain bg-white rounded-2xl select-none shadow-[0_25px_55px_-15px_rgba(0,0,0,0.28),0_15px_30px_-12px_rgba(232,81,61,0.25)]"
                >

                  <DialogPrimitive.Title className="sr-only">
                    Verificar conta BeraStays
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="sr-only">
                    Complete a verificação de identidade para acessar todos os recursos.
                  </DialogPrimitive.Description>

                  {/* Close button */}
                  <button
                    type="button"
                    aria-label="Fechar"
                    onClick={handleLater}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-foreground/60 hover:text-foreground shadow-md flex items-center justify-center transition-all"
                  >
                    <X className="w-4 h-4" strokeWidth={2.4} />
                  </button>

        {/* Header with coral gradient */}
        <div className="relative px-6 pt-7 pb-5 bg-gradient-to-br from-[#FFF3EF] via-[#FFEAE2] to-[#FFDCCF]">
          <div className="absolute inset-0 opacity-40 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 10%, rgba(232,81,61,0.18), transparent 55%), radial-gradient(circle at 90% 30%, rgba(217,79,48,0.14), transparent 60%)",
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative flex items-center gap-3"
          >
            <div className="relative">
              <span className="absolute inset-0 rounded-2xl bg-primary/30 animate-ping" />
              <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 shadow-[0_10px_25px_-10px_rgba(232,81,61,0.5)] flex items-center justify-center">
                <UserRound className="w-8 h-8 text-white" strokeWidth={2} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary/80">
                BeraStays
              </p>
              <h2 className="font-display font-bold text-[18px] leading-tight text-foreground">
                {firstName ? `Bem-vindo(a), ${firstName}` : "Bem-vindo(a) à BeraStays"}
              </h2>
            </div>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.35 }}
            className="relative mt-4 text-[13px] leading-relaxed text-foreground/75"
          >
            Verifique a sua conta para usufruir de{" "}
            <span className="font-semibold text-foreground">todos os recursos</span> da BeraStays com mais{" "}
            <span className="font-semibold text-primary">{tAuto("verifyAccount.trustHighlight", "segurança e confiança")}</span>.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="px-6 py-5">
          <div className="flex items-center gap-2 mb-3">
            <BadgeCheck className="w-4 h-4 text-primary" strokeWidth={2.2} />
            <p className="text-[11px] font-bold uppercase tracking-wider text-foreground/70">
              Passos para verificar
            </p>
          </div>

          <ol className="space-y-2.5" onPointerDown={(e) => e.stopPropagation()}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.li
                  key={step.title}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + index * 0.06, duration: 0.28 }}
                  className="flex items-start gap-3"
                >
                  <div className="relative shrink-0">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/15 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" strokeWidth={2.2} />
                    </div>
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center shadow-sm">
                      {index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-[13px] font-semibold text-foreground leading-tight">
                      {step.title}
                    </p>
                    <p className="text-[11.5px] text-muted-foreground leading-snug mt-0.5">
                      {step.desc}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 pt-1 space-y-2">
          <Button
            onClick={handleVerifyNow}
            onPointerDown={(e) => e.stopPropagation()}
            className="w-full h-11 gradient-primary text-white font-semibold rounded-xl shadow-[0_8px_20px_-8px_rgba(232,81,61,0.55)] hover:shadow-[0_10px_24px_-8px_rgba(232,81,61,0.65)] transition-all cursor-pointer"
          >
            Verificar Agora
          </Button>
          <Button
            onClick={handleLater}
            onPointerDown={(e) => e.stopPropagation()}
            variant="ghost"
            className="w-full h-10 text-[13px] font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground rounded-xl cursor-pointer"
          >
            Mais Tarde
          </Button>
        </div>
                </motion.div>
              </DialogPrimitive.Content>
            </div>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

export default VerifyAccountModal;
